import React, { useState, Fragment } from "react";
import moment from "moment";
import { FaExclamationTriangle } from "react-icons/fa";
import { Row, Tooltip } from "reactstrap";
import { isDateOlderThanDays, ReportActions } from "../../../../utils/Helpers";
import Loading from "../../../../components/Loading/Loading";

export const READ_STATUS = {
    PENDING_APPROVAL: "Pending Approval",
    NOT_APPROVED: "Not Approved",
    SUBMITTING: "Submitting",
    SUBMITTED_360: "Submitted",
    SUBMITTED_HARTREE: "Sent",
    REJECTED: "Rejected",
    RECALCULATING: "Recalculating",
    FAILED: "Failed",
    MISSING_DATA: "Missing Data",
};

const StatusFormatter = ({ cell, row }) => {
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
    const tooltipId = `tooltip-${row.id}`;

    return (
        <span style={{ display: "flex", alignItems: "center" }}>
            {READ_STATUS[cell]}
            {READ_STATUS[cell] === READ_STATUS.FAILED && (
                <Fragment>
                    <FaExclamationTriangle
                        id={tooltipId}
                        style={{ color: "red", marginLeft: "5px", cursor: "pointer" }}
                    />
                    <Tooltip
                        isOpen={tooltipOpen}
                        target={tooltipId}
                        toggle={toggleTooltip}
                    >
                        {row.error_message || ""}
                    </Tooltip>
                </Fragment>
            )}
        </span>
    );
};

const hideSortIcon = (column) => (
    <div className="custom-header">
        {column.text}
        <span className="order" style={{ display: "none" }}>
            <span className="dropdown">
                <span className="caret"></span>
            </span>
            <span className="dropup">
                <span className="caret"></span>
            </span>
        </span>
    </div>
);

const setThousandSeparator = (cell) =>
    cell != null ? cell.toLocaleString("en") : "";

const editableStatuses = [READ_STATUS.PENDING_APPROVAL, READ_STATUS.FAILED];

const isCellEditable = (row) =>
    editableStatuses.includes(READ_STATUS[row.status]);

const isCellEdited = (row, recalculatedRows, dataField) => {
    return recalculatedRows.some(
        (recalcRow) =>
            recalcRow.gas_reading_id === row.id && recalcRow[dataField] !== null
    );
};

const getCellEditableClasses = (row, recalculatedRows, dataField) => {
    return `${isCellEditable(row) ? "editable-cell" : undefined} ${isCellEdited(row, recalculatedRows, dataField) ? "edited-cell" : ""
        }`;
};

export const columns = (submitReading, recalculatedRows, forCSV = false) => [
    {
        dataField: "date",
        text: "Date",
        formatter: (cell) => moment(cell).format("DD/MM/YYYY"),
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        sort: true,
        editable: false,
    },
    {
        dataField: "site",
        text: "Site",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        sort: true,
        editable: false,
    },
    {
        dataField: "mprn",
        text: "MPRN",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        sort: true,
        editable: false,
    },
    {
        dataField: "meter_reading",
        text: "New meter reading",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        classes: (cell, row) =>
            getCellEditableClasses(row, recalculatedRows, "meter_reading"),
        formatter: setThousandSeparator,
        sort: true,
        editable: (cell, row) => isCellEditable(row),
    },
    {
        dataField: "uncorrected_rd1_reading",
        text: "Uncorrected RD1 Read",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        classes: (cell, row) =>
            getCellEditableClasses(row, recalculatedRows, "uncorrected_rd1_reading"),
        formatter: setThousandSeparator,
        sort: true,
        editable: (cell, row) =>
            isCellEditable(row) && row.convertor_serial_number !== null,
    },
    {
        dataField: "corrected_rd1_reading",
        text: "Corrected RD1 Read",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        classes: (cell, row) =>
            getCellEditableClasses(row, recalculatedRows, "corrected_rd1_reading"),
        formatter: setThousandSeparator,
        sort: true,
        editable: (cell, row) =>
            isCellEditable(row) && row.convertor_serial_number !== null,
    },
    ...(forCSV
        ? [
            {
                dataField: "supply_generation",
                text: "Supply Gen",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "class",
                text: "Class",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "imperial_metric",
                text: "Imperial Metric",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "conversion_factor",
                text: "Conversion Factor",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "cv",
                text: "CV",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "joules_to_watts",
                text: "Joules to watts",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "uncorrected_volume",
                text: "Uncorrected volume",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "corrected_volume",
                text: "Corrected volume",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "correction_factor",
                text: "Correction factor",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
        ]
        : []),
    {
        dataField: "uncorrected_rd1_consumption",
        text: "Uncorrected RD1 Consumption (kWh)",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        style: (col, row) => {
            return parseFloat(row.uncorrected_cns_consumption) !==
                parseFloat(row.uncorrected_rd1_consumption) &&
                row.status !== "RECALCULATING" &&
                row.status !== "MISSING_DATA"
                ? { background: "#f5c0c0" }
                : {};
        },
        formatter: (cell, row) => {
            if (cell === null) return "";
            if (row.status === "RECALCULATING")
                return <Loading skipCurrentClass skipCurrentText />;
            return setThousandSeparator(parseInt(cell.toFixed()));
        },
        sort: true,
        editable: false,
    },
    {
        dataField: "corrected_rd1_consumption",
        text: "Corrected RD1 Consumption (kWh)",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        style: (col, row) => {
            return parseFloat(row.corrected_cns_consumption) !==
                parseFloat(row.corrected_rd1_consumption) &&
                row.status !== "RECALCULATING" &&
                row.status !== "MISSING_DATA"
                ? { background: "#f5c0c0" }
                : {};
        },
        formatter: (cell, row) => {
            if (cell === null) return "";
            if (row.status === "RECALCULATING")
                return <Loading skipCurrentClass skipCurrentText />;
            return setThousandSeparator(parseInt(cell.toFixed()));
        },
        sort: true,
        editable: false,
    },
    {
        dataField: "uncorrected_cns_consumption",
        text: "Uncorrected CNS Consumption (kWh)",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        style: (col, row) => {
            return parseFloat(row.uncorrected_cns_consumption) !==
                parseFloat(row.uncorrected_rd1_consumption) &&
                row.status !== "MISSING_DATA"
                ? { background: "#f5c0c0" }
                : {};
        },
        classes: (cell, row) =>
            getCellEditableClasses(
                row,
                recalculatedRows,
                "uncorrected_cns_consumption"
            ),
        formatter: (cell) => {
            if (cell === null) return "";
            const cellValue = typeof cell === "string" ? parseFloat(cell) : cell;
            return setThousandSeparator(parseInt(cellValue.toFixed()));
        },
        sort: true,
        editable: (cell, row) => isCellEditable(row),
    },
    {
        dataField: "corrected_cns_consumption",
        text: "Corrected CNS Consumption (kWh)",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        style: (col, row) => {
            return parseFloat(row.corrected_cns_consumption) !==
                parseFloat(row.corrected_rd1_consumption) &&
                row.status !== "MISSING_DATA"
                ? { background: "#f5c0c0" }
                : {};
        },
        classes: (cell, row) =>
            getCellEditableClasses(
                row,
                recalculatedRows,
                "corrected_cns_consumption"
            ),
        formatter: (cell) => {
            if (cell === null) return "";
            const cellValue = typeof cell === "string" ? parseFloat(cell) : cell;
            return setThousandSeparator(parseInt(cellValue.toFixed()));
        },
        sort: true,
        editable: (cell, row) => isCellEditable(row),
    },
    {
        dataField: "allocation_value",
        text: "Allocation",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        formatter: (cell) => (cell !== null ? cell : "N/A"),
        sort: true,
        editable: false,
    },
    {
        dataField: "total_power_exported",
        text: "Total Power Exported",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        formatter: (cell, row) => {
            if (cell === null) return "";
            const cellValue = typeof cell === "string" ? parseFloat(cell) : cell;
            return setThousandSeparator(parseInt(cellValue.toFixed()));
        },
        sort: true,
        editable: false,
    },
    {
        dataField: "efficiency",
        text: "Efficiency[%]",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        style: (cell, row) => {
            const bgColor = row.is_efficiency_in_range ? "#d9fcee" : "#fae1f6";
            const efficiencyMethodColor =
                row.corrected_cns_consumption > 0
                    ? "#000000"
                    : row.corrected_rd1_consumption > 0
                        ? "#667bb9"
                        : bgColor;

            return {
                marginRight: "0.5em",
                ...(row.status !== "RECALCULATING" && row.status !== "MISSING_DATA"
                    ? {
                        backgroundColor: bgColor,
                        boxShadow: `inset 5px 0 ${efficiencyMethodColor}`,
                        border: "none",
                    }
                    : {}),
            };
        },
        formatter: (cell, row) => {
            if (cell === null) return "";
            if (row.status === "RECALCULATING")
                return <Loading skipCurrentClass skipCurrentText />;
            return cell;
        },
        sort: true,
        sortFunc: (a, b, order, dataField, rowA, rowB) =>
			parseFloat(b) - parseFloat(a),
        editable: false,
    },
    {
        dataField: "approve",
        text: "Approve",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        formatter: (cell, row) => {
            const displaySendBtn =
                parseFloat(row.corrected_rd1_reading) !== 0 ||
                parseFloat(row.uncorrected_rd1_reading) !== 0 ||
                parseFloat(row.meter_reading) !== 0;

            return (
                <Row className="justify-content-around">
                    {displaySendBtn &&
                        isDateOlderThanDays(row.date, 6) &&
                        READ_STATUS[row.status] !== READ_STATUS.REJECTED &&
                        READ_STATUS[row.status] !== READ_STATUS.SUBMITTING &&
                        READ_STATUS[row.status] !== READ_STATUS.SUBMITTED_360 &&
                        READ_STATUS[row.status] !== READ_STATUS.SUBMITTED_HARTREE &&
                        READ_STATUS[row.status] !== READ_STATUS.RECALCULATING &&
                        READ_STATUS[row.status] !== READ_STATUS.MISSING_DATA && (
                            <button
                                onClick={() => submitReading(row.id, ReportActions.Approve)}
                            >
                                <i className="icon ion-android-send"></i>
                            </button>
                        )
                    }
                </Row>
            );
        },
        editable: false,
    },
    {
        dataField: "status",
        text: "Status",
        headerFormatter: hideSortIcon,
        headerStyle: { whiteSpace: "unset" },
        formatter: (cell, row) => <StatusFormatter cell={cell} row={row} />,
        style: () => ({ paddingRight: "10px" }),
        sort: true,
        editable: false,
    }, ...(forCSV
        ? [
            {
                dataField: "soq_value",
                text: "SOQ",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
            {
                dataField: "actual_back_calc",
                text: "Actual Or Back-Calc",
                headerFormatter: hideSortIcon,
                headerStyle: { whiteSpace: "unset" },
                formatter: setThousandSeparator,
                sort: true,
                editable: false,
            },
        ]
        : []),
];
